/**
 * Responsibility: hold the registry of available biomes.
 *
 * Adding a biome means importing it here and adding it to the list. Nothing else needs
 * to change: the biome field derives its own noise from the id, the heightfield blends
 * whatever it is given, and the palette reads colours off the descriptor.
 */

import { mountains } from './mountains.js';
import { baseline } from './baseline.js';
import { rollingHills } from './rollingHills.js';
import { farmland } from './farmland.js';
import { mesa } from './mesa.js';
import { canyon } from './canyon.js';
import { badlands } from './badlands.js';
import { river } from './river.js';
import { lakeland } from './lakeland.js';
import { rollingLakes } from './rollingLakes.js';
import { woodedLakes } from './woodedLakes.js';
import { city } from './city.js';
import { forest } from './forest.js';
import { suburbia } from './suburbia.js';
import { industrial } from './industrial.js';
import { park } from './park.js';
import { vineyard } from './vineyard.js';

/**
 * Every biome in the world, in no particular order.
 *
 * Exactly one biome should declare `constantSuitability`, making it the default that fills
 * in wherever nothing else wants to be. That is `baseline`.
 *
 * **Adding a biome squeezes every other one**, including the default. `BASELINE.SUITABILITY` is the
 * thumb on the scale for how much open ground is left between regions, and it has to be re-measured and
 * usually raised each time this list grows — otherwise the world fills up and nothing reads as arriving
 * anywhere.
 */
export const BIOMES = [
  mountains,
  baseline,
  rollingHills,
  farmland,
  mesa,
  canyon,
  badlands,
  river,
  lakeland,
  rollingLakes,
  woodedLakes,
  city,
  forest,
  suburbia,
  industrial,
  park,
  vineyard,
];

/**
 * Look up a biome descriptor by id.
 *
 * @param {string} id
 * @returns {object} The biome descriptor.
 * @throws {Error} If no biome with that id is registered, because silently returning
 *   undefined would surface much later as unexplained flat terrain.
 */
export function getBiome(id) {
  const biome = BIOMES.find((candidate) => candidate.id === id);
  if (!biome) {
    throw new Error(`getBiome: no biome registered with id "${id}"`);
  }
  return biome;
}

/**
 * Validate that a descriptor has everything the rest of the system expects.
 * Called by tests, and by the registry check below, so a malformed biome fails loudly
 * at startup rather than producing subtly wrong terrain.
 *
 * @param {object} biome
 * @returns {boolean}
 */
export function isValidBiome(biome) {
  if (
    !biome ||
    typeof biome.id !== 'string' ||
    biome.id.length === 0 ||
    typeof biome.label !== 'string' ||
    typeof biome.createElevation !== 'function' ||
    typeof biome.maxElevation !== 'number' ||
    !(biome.maxElevation > 0) ||
    !biome.palette ||
    !Number.isFinite(biome.palette.LOW) ||
    !Number.isFinite(biome.palette.HIGH)
  ) {
    return false;
  }

  /*
    Optional suitability properties, but if present they must be usable. A `NaN` bias would
    poison every comparison in the biome field and make the offending biome either always
    win or never appear, with nothing pointing at the cause.
  */
  if (biome.constantSuitability !== undefined && !Number.isFinite(biome.constantSuitability)) {
    return false;
  }
  if (biome.suitabilityBias !== undefined && !Number.isFinite(biome.suitabilityBias)) {
    return false;
  }

  // Optional, but a non-function here would fail deep inside tile assembly instead of at boot.
  if (biome.createStructures !== undefined && typeof biome.createStructures !== 'function') {
    return false;
  }

  /*
    Optional separate gradient for a biome's structures. If declared it must be usable, because a
    malformed one produces NaN colours that render as invisible geometry with no error anywhere.
  */
  if (biome.structurePalette !== undefined) {
    if (
      !biome.structurePalette ||
      !Number.isFinite(biome.structurePalette.LOW) ||
      !Number.isFinite(biome.structurePalette.HIGH)
    ) {
      return false;
    }
  }

  return true;
}

// Fail fast on a malformed or duplicated registry entry.
for (const biome of BIOMES) {
  if (!isValidBiome(biome)) {
    throw new Error(`biomes/index: invalid biome descriptor "${biome?.id ?? 'unknown'}"`);
  }
}
if (new Set(BIOMES.map((b) => b.id)).size !== BIOMES.length) {
  throw new Error('biomes/index: duplicate biome id in registry');
}

/*
  More than one default would mean two biomes both claiming to fill in the gaps, which is
  not meaningful — they would simply compete like any other pair and the world would lose
  its connective tissue.
*/
if (BIOMES.filter((biome) => biome.constantSuitability !== undefined).length > 1) {
  throw new Error('biomes/index: only one biome may declare constantSuitability');
}


